"use client";

import { usePostHog } from "posthog-js/react";
import { motion, useReducedMotion } from "@/lib/motion";
import { ArrowRight, BadgeCheck, ShieldCheck, Banknote, Gift } from "@/components/icons";
import { cn, scrollToId } from "@/lib/utils";
import { buttonVariants, HeroGlow } from "@/components/ui";
import ReviewBadges from "@/components/ui/review-badges";
import { REGISTER_URL, FREE_TRIAL_LABEL } from "@/lib/constants";
import { PRICING_VERIFIED_DATE } from "./pricing-data";

const MotionDiv = motion.div;

const TRUST_CHIPS = [
  { icon: ShieldCheck, label: "SOC 2 Type II" },
  { icon: BadgeCheck, label: "ISO 27001" },
  { icon: Banknote, label: "30-day money-back guarantee" },
  { icon: Gift, label: "Free sign-up" },
];

export default function DataimpulseAlternativeHero() {
  const prefersReducedMotion = useReducedMotion();
  const posthog = usePostHog();

  const fadeUp = (delay = 0) => ({
    initial: prefersReducedMotion ? {} : { opacity: 0, y: 24 },
    animate: prefersReducedMotion ? {} : { opacity: 1, y: 0 },
    transition: { duration: 0.55, delay },
  });

  return (
    <section
      className="border-border relative overflow-y-clip border-b pt-32 pb-20"
      aria-labelledby="dataimpulse-alternative-heading"
    >
      <HeroGlow align="left" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv {...fadeUp(0)}>
          <p className="text-muted-foreground mb-3 text-sm font-medium">Alternatives</p>
        </MotionDiv>

        <MotionDiv {...fadeUp(0.08)}>
          <h1
            id="dataimpulse-alternative-heading"
            className="text-foreground mb-8 max-w-4xl text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
          >
            DataImpulse Alternative: Proxidize vs DataImpulse
          </h1>
        </MotionDiv>

        <MotionDiv {...fadeUp(0.16)} className="max-w-3xl">
          <p className="text-muted-foreground light:text-foreground/80 text-base leading-relaxed">
            Proxidize and DataImpulse both offer mobile proxies starting at $2/GB, but targeting
            changes the comparison. DataImpulse bills state, city, ZIP, and ASN selection at twice
            the standard rate. Proxidize includes city and ASN targeting in its $2/GB mobile plans.
            Compare the total cost for your required locations, targeting settings, and usage,
            alongside minimum purchases and bandwidth expiration.
          </p>
        </MotionDiv>

        <MotionDiv {...fadeUp(0.24)} className="mt-10 flex flex-wrap items-center gap-3">
          <a
            href={REGISTER_URL}
            onClick={() =>
              posthog?.capture("alternatives_cta_clicked", {
                competitor: "dataimpulse",
                location: "hero",
              })
            }
            className={cn(buttonVariants({ variant: "default", size: "lg" }))}
          >
            {FREE_TRIAL_LABEL}
            <ArrowRight size={16} className="ml-2" aria-hidden="true" />
          </a>
          <button
            type="button"
            onClick={() => scrollToId("cost-calculator")}
            className={cn(buttonVariants({ variant: "outline", size: "lg" }), "cursor-pointer")}
          >
            Compare your monthly cost
          </button>
        </MotionDiv>

        <MotionDiv {...fadeUp(0.32)} className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-3">
          {TRUST_CHIPS.map(({ icon: Icon, label }) => (
            <div key={label} className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <Icon size={13} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
              {label}
            </div>
          ))}
        </MotionDiv>

        <MotionDiv {...fadeUp(0.4)} className="mt-6 flex flex-wrap items-center gap-6">
          <ReviewBadges />
        </MotionDiv>

        <MotionDiv {...fadeUp(0.48)}>
          <p className="text-muted-foreground mt-8 text-xs">
            All DataImpulse figures come from their public pages. Pricing last verified on{" "}
            {PRICING_VERIFIED_DATE}. Always confirm current pricing on each provider&rsquo;s site.
          </p>
        </MotionDiv>
      </div>
    </section>
  );
}
